/**
 * Onix Cart — shared utilities
 * ─────────────────────────────
 * Exposes `window.OnixCart` as the shared namespace for helpers that need
 * to be reachable from every cart-related script (cart-drawer.js,
 * cart-drawer-quickadd.js, cart-drawer-discount.js).
 *
 * This file is the ANTIDOTE to the loggedFetch scope regression: by
 * putting utilities on a true global namespace instead of relying on
 * IIFE closure scope, nested functions cannot accidentally shadow them
 * and separate scripts cannot fail with ReferenceError.
 *
 * Load order in layout/theme.liquid:
 *   1. cart-drawer-utils.js   (this file — defines window.OnixCart)
 *   2. cart-drawer.js         (main drawer logic)
 *   3. cart-drawer-quickadd.js
 *   4. cart-drawer-discount.js
 *
 * All four are `defer`ed so execution order matches DOM order.
 */
(function () {
  if (window.OnixCart && window.OnixCart.__initialized) return;

  /* ── Named timing constants ──────────────────────────────
     Previously scattered as magic numbers (4000, 320, etc.)
     inside setTimeout calls. Centralised so designers who
     tweak the toast animation timing only touch one place. */
  const TOAST_DISPLAY_MS = 4000;   // how long the toast stays fully opaque
  const TOAST_FADE_MS    = 320;    // opacity transition duration

  /**
   * HTML-escape a string for safe interpolation into innerHTML.
   * Returns empty string on null/undefined.
   */
  const escapeHtml = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  /**
   * Drop-in fetch wrapper that silently logs non-OK responses. No
   * behavior change vs native fetch: always returns the Response object,
   * never throws on !ok, so existing call sites that ignore the response
   * keep working.
   */
  const loggedFetch = async (url, opts) => {
    const res = await fetch(url, opts);
    if (!res.ok) {
      try { console.warn('[onix-cart] fetch non-ok', res.status, url); } catch (e) { /* noop */ }
    }
    return res;
  };

  /**
   * Show a transient error toast at the bottom of the viewport.
   * Auto-dismisses after 4s. Removes any pre-existing toast first.
   */
  function showCartError(message) {
    const existing = document.getElementById('onix-cart-error-toast');
    if (existing) existing.remove();
    const toast = document.createElement('div');
    toast.id = 'onix-cart-error-toast';
    toast.setAttribute('role', 'alert');
    toast.style.cssText = [
      'position:fixed', 'bottom:1.5rem', 'left:50%', 'transform:translateX(-50%)',
      'z-index:10000', 'background:#1a1a1a', 'color:#fff',
      'padding:0.85rem 1.4rem', 'border-radius:0.75rem',
      'border:1px solid rgba(255,100,100,0.4)',
      'font-size:0.875rem', 'font-weight:500',
      'box-shadow:0 8px 32px rgba(0,0,0,0.4)',
      'max-width:min(90vw,28rem)', 'text-align:center',
      'animation:onix-toast-in 0.25s ease both'
    ].join(';');
    toast.textContent = message;
    if (!document.getElementById('onix-toast-style')) {
      const s = document.createElement('style');
      s.id = 'onix-toast-style';
      s.textContent = '@keyframes onix-toast-in{from{opacity:0;transform:translateX(-50%) translateY(8px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}';
      document.head.appendChild(s);
    }
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity ' + (TOAST_FADE_MS / 1000) + 's';
      setTimeout(() => toast.remove(), TOAST_FADE_MS);
    }, TOAST_DISPLAY_MS);
  }

  /**
   * Read the translation strings attached to [data-cart-drawer-root] as
   * data-t-* attributes. Returns the same object shape every time, with
   * English fallbacks so scripts that run before the drawer exists
   * still get sane defaults.
   */
  function getDrawerStrings() {
    const dr = document.querySelector('[data-cart-drawer-root]');
    return {
      emptyTitle:   (dr && dr.dataset.tEmptyTitle) || 'Your bag is empty',
      emptyCopy:    (dr && dr.dataset.tEmptyCopy) || 'Explore our selection.',
      continue:     (dr && dr.dataset.tContinue) || 'Continue shopping',
      remove:       (dr && dr.dataset.tRemove) || 'Remove',
      quantity:     (dr && dr.dataset.tQuantity) || 'Quantity controls',
      addToCart:    (dr && dr.dataset.tAddToCart) || 'ADD TO CART',
      apply:        (dr && dr.dataset.tApply) || 'Apply',
      alreadyUsed:  (dr && dr.dataset.tAlreadyUsed) || 'Already used',
      error:        (dr && dr.dataset.tError) || 'Error',
      chooseSize:   (dr && dr.dataset.tChooseSize) || 'Choose your size:',
      flocage:      (dr && dr.dataset.tFlocage) || 'Flocage',
      inStock:      (dr && dr.dataset.tInStock) || 'In stock',
      bundleLabel:  (dr && dr.dataset.tBundleLabel) || 'Offre Découverte',
    };
  }

  var cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  if (cartRoot.slice(-1) !== '/') cartRoot += '/';
  window.OnixCart = {
    __initialized: true,
    cartRoot: cartRoot,
    escapeHtml,
    loggedFetch,
    showCartError,
    getDrawerStrings,
  };

  /* Global numeric-input enforcement — installed once at utils load time
     so it applies on EVERY page where the script runs, not just on product
     pages. Fixes a regression where the quickadd popup opened from a
     collection / homepage accepted letters because product.liquid's
     {% javascript %} block wasn't loaded on those pages.

     Contract: any <input data-numeric-max="N"> accepts only digits and
     is capped at N characters. Works for type="text", type="number", and
     type="tel" inputs — whatever the markup uses. */
  document.addEventListener('input', function (e) {
    var el = e.target;
    if (!el || !el.matches || !el.matches('input[data-numeric-max]')) return;
    var max = parseInt(el.dataset.numericMax, 10) || 3;
    var cleaned = String(el.value).replace(/[^0-9]/g, '').slice(0, max);
    if (cleaned !== el.value) el.value = cleaned;
  });

  /* Complementary keydown guard: block non-digit key presses upfront
     (for type="text" where the input event strips AFTER the keystroke,
     which can cause a visible flicker). Allows navigation/edit keys. */
  document.addEventListener('keydown', function (e) {
    var el = e.target;
    if (!el || !el.matches || !el.matches('input[data-numeric-max]')) return;
    // Allow control keys
    var allow = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    if (allow.indexOf(e.key) !== -1) return;
    // Allow copy/paste/cut/select-all combos
    if (e.ctrlKey || e.metaKey) return;
    // Block anything that's not a digit
    if (!/^[0-9]$/.test(e.key)) { e.preventDefault(); return; }
    // Block extra digits beyond max
    var max = parseInt(el.dataset.numericMax, 10) || 3;
    if (el.value.length >= max && el.selectionStart === el.selectionEnd) {
      e.preventDefault();
    }
  });
})();

/* ── Progress bar visuals (auto-apply removed) ──────────────────
   Reads tier data from #onix-discount-bar and updates the visual
   fill + dot states as the cart count changes. The previous
   auto-apply behaviour (fetch('/discount/CODE') when count crosses a
   tier) was removed — discount application is now opt-in via the
   #onix-promo-suggest popup in the cart drawer. */
(function () {
  var bar = document.getElementById('onix-discount-bar');
  if (!bar) return;
  var tiers;
  try { tiers = JSON.parse(bar.dataset.tiers || '[]'); } catch (e) { return; }
  if (!tiers.length) return;

  function syncVisuals() {
    fetch(((window.OnixCart && window.OnixCart.cartRoot) || '/') + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* Bundle items (added via the OFFRE popup, marked with
           `_offer_bundle:true`) are part of their own discount (oniix)
           and do NOT count toward the tier popup thresholds. The bar
           must therefore mirror the merchant rule and subtract them
           from item_count before computing the fill — otherwise the
           customer sees the bar flicker UP to a tier when they pick
           the bundle, then DOWN once the section's main JS corrects
           it. The fallback hint set by cart-drawer.js
           (`__onixOptimisticBundleQty`) covers the brief Shopify
           write race where properties haven't landed on /cart.js yet. */
        var bundleCount = (cart.items || []).reduce(function (sum, i) {
          var isBundle = i.properties && i.properties._offer_bundle === 'true';
          return sum + (isBundle ? (i.quantity || 0) : 0);
        }, 0);
        var optimisticQty = Number(window.__onixOptimisticBundleQty || 0);
        var optimisticUntil = Number(window.__onixOptimisticBundleUntil || 0);
        var optimisticActive = optimisticQty > 0 && Date.now() < optimisticUntil;
        var effectiveBundle = optimisticActive
          ? Math.max(bundleCount, optimisticQty)
          : bundleCount;
        var nonBundleCount = Math.max(0, (cart.item_count || 0) - effectiveBundle);
        updateBarVisuals(nonBundleCount);
      })
      .catch(function () { /* swallow */ });
  }

  /* Update the bar fill + dots visually (bypasses CDN-cached section JS) */
  function updateBarVisuals(count) {
    var fillEl = document.getElementById('onix-bar-fill');
    var fillExp = document.getElementById('onix-bar-fill-exp');
    if (!fillEl) return;

    var activeIdx = -1;
    for (var i = tiers.length - 1; i >= 0; i--) {
      if (count >= tiers[i].qty) { activeIdx = i; break; }
    }

    /* Tier-based fill: each dot = 20%, fill reaches dot on tier match */
    var pct = 0;
    if (activeIdx >= 0) {
      var dotPos = (activeIdx + 1) * 20;
      if (activeIdx < tiers.length - 1) {
        var progress = (count - tiers[activeIdx].qty) / (tiers[activeIdx + 1].qty - tiers[activeIdx].qty);
        pct = dotPos + (progress * 20);
      } else {
        pct = 100;
      }
    } else if (count > 0) {
      pct = (count / tiers[0].qty) * 20;
    }

    fillEl.style.width = pct + '%';
    if (fillExp) fillExp.style.width = pct + '%';

    bar.querySelectorAll('[data-dot], [data-dot-exp]').forEach(function (dot) {
      var idx = parseInt(dot.dataset.dot || dot.dataset.dotExp, 10) - 1;
      if (idx < tiers.length) dot.classList.toggle('is-reached', count >= tiers[idx].qty);
    });

    bar.querySelectorAll('.onix-bar__tier').forEach(function (el, idx) {
      el.classList.toggle('is-active', idx === activeIdx);
    });
  }

  /* Trigger visual update on cart change. */
  document.addEventListener('cart:refresh', syncVisuals);

  var countNode = document.querySelector('[data-cart-count]');
  if (countNode) {
    var prev = countNode.textContent.trim();
    new MutationObserver(function () {
      var c = countNode.textContent.trim();
      if (c !== prev) { prev = c; syncVisuals(); }
    }).observe(countNode, { childList: true, characterData: true, subtree: true });
  }

  /* Initial render */
  syncVisuals();
})();
